/**
 * Build a deterministic keyword JQL query of the form `textfields ~ "<query>*"`.
 *
 * This mirrors the NIN translation strategy used when a query goes from Quick
 * Find to the new Issue Navigator and is the FE counterpart to the backend's
 * `JIRA_NLQ_KEYWORD` suppression path introduced in xp-knowledge-discovery
 * PR #1611.
 *
 * The user query is sanitised before being interpolated so that the resulting
 * JQL is always well-formed AND only the trailing `*` we explicitly append is
 * treated as a wildcard. Specifically:
 *
 *  - Surrounding whitespace is trimmed.
 *  - Backslashes are escaped first (`\` → `\\`).
 *  - Double quotes are escaped so the surrounding quoted JQL string stays
 *    valid (`"` → `\"`).
 *  - The Lucene/JQL wildcard characters `*` and `?` inside the user query are
 *    escaped (`*` → `\*`, `?` → `\?`) so they are matched literally and do
 *    not turn into unintended wildcards in the middle of a term. For example,
 *    the input `fix * login` becomes `textfields ~ "fix \* login*"` instead
 *    of `textfields ~ "fix * login*"`, which would otherwise match far more
 *    issues than the user intended.
 *
 * If the query is empty or whitespace-only the function returns `null` so the
 * caller can decide how to handle the no-op case (e.g. surface an error card).
 */
export const buildKeywordJql = (rawQuery: string): string | null => {
	const trimmed = rawQuery.trim();
	if (trimmed.length === 0) {
		return null;
	}

	// Escape backslashes first so the subsequent escapes don't get re-escaped,
	// then escape double quotes (string delimiter) and the JQL wildcards `*`
	// and `?` so user input is matched literally inside the quoted Lucene
	// query. The only wildcard we want in the resulting JQL is the trailing
	// `*` that we append ourselves.
	const escaped = trimmed
		.replace(/\\/g, '\\\\')
		.replace(/"/g, '\\"')
		.replace(/\*/g, '\\*')
		.replace(/\?/g, '\\?');

	return `textfields ~ "${escaped}*"`;
};
